/**
 * Simple in-memory cache for API responses to improve navigation speed.
 * Data expires after a configurable TTL to ensure freshness.
 */

interface CacheEntry {
  data: any;
  expiry: number;
}

const cache = new Map<string, CacheEntry>();
const DEFAULT_TTL = 30000; // 30 seconds default

export const dataCache = {
  /** Get cached data if still valid */
  get(key: string): any | null {
    const entry = cache.get(key);
    if (!entry) return null;

    if (Date.now() > entry.expiry) {
      cache.delete(key);
      return null;
    }

    return entry.data;
  },

  /** Store data in cache */
  set(key: string, data: any, ttl: number = DEFAULT_TTL): void {
    cache.set(key, {
      data,
      expiry: Date.now() + ttl,
    });
  },

  /** Invalidate specific cache entry */
  invalidate(key: string): void {
    cache.delete(key);
  },

  /** Invalidate all cache entries matching a prefix */
  invalidatePrefix(prefix: string): void {
    for (const key of cache.keys()) {
      if (key.startsWith(prefix)) {
        cache.delete(key);
      }
    }
  },

  /** Clear entire cache */
  clear(): void {
    cache.clear();
  },
};

/**
 * Cached fetch wrapper - returns cached data if available, otherwise fetches.
 * Only GET requests are cached. Returns a Response-compatible object.
 */
export async function cachedFetch(
  url: string,
  options: RequestInit = {},
  ttl: number = DEFAULT_TTL
): Promise<any> {
  const cacheKey = `${options.method || 'GET'}:${url}`;

  if (!options.method || options.method === 'GET') {
    const cached = dataCache.get(cacheKey);
    if (cached) {
      return {
        ok: true,
        status: 200,
        json: async () => cached,
        text: async () => JSON.stringify(cached),
        _fromCache: true,
      };
    }
  }

  const response = await fetch(url, options);

  if (response.ok && (!options.method || options.method === 'GET')) {
    const data = await response.clone().json();
    dataCache.set(cacheKey, data, ttl);
  }

  return response;
}

export default dataCache;
